import React, { useState, useEffect } from "react";
import axios from "axios";
import { Database, Mail, Search, BarChart3, LineChart, Settings2, Users, Send } from "lucide-react";
import { Card, fmtN } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function UsersCard({ headers, toast }) {
  const [users, setUsers] = useState(null);
  const [invEmail, setInvEmail] = useState("");
  const [invRole, setInvRole] = useState("admin");
  const [sending, setSending] = useState(false);

  const load = () => axios.get(`${API}/admin/users`, headers()).then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const invite = async (e) => {
    e.preventDefault();
    if (!invEmail.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/users/invite`, { email: invEmail.trim(), role: invRole }, headers());
      toast({ title: "Invite sent", description: `${invEmail.trim()} got a set-password email (valid 72h).` });
      setInvEmail("");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({ title: "Couldn't send invite", description: typeof detail === "string" ? detail : "Check the email and try again." });
    } finally {
      setSending(false);
    }
  };

  return (
    <Card title="Dashboard Users" right={<Users size={15} className="text-neutral-500" />} testid="conn-users">
      <div className="divide-y divide-white/5 mb-5">
        {(users || []).map((u) => (
          <div key={u.email} className="flex items-center justify-between gap-3 py-2.5" data-testid={`user-row-${u.email.replace(/[@.]/g, "-")}`}>
            <div>
              <div className="text-[13px] text-white font-medium">{u.email}</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-neutral-500 mt-0.5">
                {u.role}{u.last_login ? ` · last login ${new Date(u.last_login).toLocaleDateString()}` : ""}
              </div>
            </div>
            <span className={`font-mono text-[9px] uppercase tracking-[0.12em] px-2 py-1 border ${u.has_password ? "text-emerald-400 border-emerald-400/30" : "text-amber-400 border-amber-400/30"}`}>
              {u.has_password ? "Active" : "Invite pending"}
            </span>
          </div>
        ))}
        {users && users.length === 0 && <p className="py-2 text-[13px] text-neutral-500">No users yet.</p>}
      </div>
      <form onSubmit={invite} className="flex gap-2">
        <input
          type="email"
          value={invEmail}
          onChange={(e) => setInvEmail(e.target.value)}
          placeholder="coach@email.com"
          data-testid="invite-email-input"
          className="flex-1 bg-[#121214] border border-white/10 focus:border-[#9D0423] focus:outline-none text-white text-[13px] px-3 py-2"
        />
        <select value={invRole} onChange={(e) => setInvRole(e.target.value)} data-testid="invite-role-select" className="bg-[#121214] border border-white/10 text-white text-[12px] px-2 focus:outline-none focus:border-[#9D0423]">
          <option value="admin">Admin</option>
          <option value="coach">Coach</option>
          <option value="owner">Owner</option>
        </select>
        <button type="submit" disabled={sending} data-testid="invite-send" className="inline-flex items-center gap-1.5 bg-[#9D0423] hover:bg-[#7E031C] disabled:opacity-50 text-white font-mono text-[10px] uppercase tracking-[0.12em] px-3.5 transition-colors">
          <Send size={11} /> {sending ? "Sending…" : "Invite"}
        </button>
      </form>
      <p className="mt-3 text-[10px] text-neutral-600">Invites email a one-time "Set your password" link (valid 72 hours) from the verified bostonirongrittraining.com sender.</p>
    </Card>
  );
}

function StatusPill({ ok, okLabel = "Connected", badLabel = "Not connected" }) {
  return (
    <span className={`font-mono text-[9px] uppercase tracking-[0.14em] px-2 py-1 border ${ok ? "text-emerald-400 border-emerald-400/30 bg-emerald-400/5" : "text-neutral-500 border-white/10"}`}>
      {ok ? okLabel : badLabel}
    </span>
  );
}

const inputCls = "w-full bg-[#121214] border border-white/10 focus:border-[#9D0423] focus:outline-none text-white text-[13px] px-3 py-2";
const labelCls = "block font-mono text-[9px] uppercase tracking-[0.16em] text-neutral-500 mb-1";

export default function Connections({ headers, range, refreshKey, leads, settings, saveSettings, toast }) {
  const [gsc, setGsc] = useState(null);
  const [ga4, setGa4] = useState(null);
  const [sm, setSm] = useState(null);
  const [gen, setGen] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/dashboard/gsc?range=${range}`, headers()).then((r) => setGsc(r.data)).catch(() => setGsc({ connected: false }));
    axios.get(`${API}/admin/dashboard/ga4?range=${range}`, headers()).then((r) => setGa4(r.data)).catch(() => setGa4({ connected: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, refreshKey]);

  useEffect(() => {
    if (settings && !sm) {
      setSm({ ...settings.semrush });
      setGen({ avg_membership_value: settings.avg_membership_value, speed_to_lead_target: settings.speed_to_lead_target });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const mcSynced = leads.filter((l) => l.mailchimp_status === "success").length;
  const mcLast = leads.find((l) => l.mailchimp_status);

  const cards = [
    {
      icon: Database, title: "MongoDB Leads", ok: true, testid: "conn-mongo",
      body: `${fmtN(leads.length)} leads stored. Contacts, waitlist, challenge and subscribers live in the same database.`,
    },
    {
      icon: Mail, title: "Mailchimp", ok: mcSynced > 0, testid: "conn-mailchimp",
      body: `Embedded form f_id 00bf43edf0 · ${fmtN(mcSynced)} leads synced. Last sync result: ${mcLast ? `${mcLast.mailchimp_status}${mcLast.mailchimp_msg ? ` — ${mcLast.mailchimp_msg}` : ""}` : "no syncs yet"}.`,
    },
    {
      icon: Search, title: "Google Search Console", ok: !!gsc?.connected, testid: "conn-gsc",
      body: gsc?.connected ? `Reading ${gsc.site_url}.` : gsc?.error || "Add GOOGLE_SERVICE_ACCOUNT_JSON (and optionally GSC_SITE_URL) to connect clicks, impressions and rankings.",
    },
    {
      icon: BarChart3, title: "Google Analytics 4", ok: !!ga4?.connected, testid: "conn-ga4",
      body: ga4?.connected ? `Property ${"" + (ga4.property_id || "connected")} · tag G-5Q3L96NZ5P.` : ga4?.error || `Site is tagged with G-5Q3L96NZ5P (property ${ga4?.property_id || "549599034"}). Paste GOOGLE_SERVICE_ACCOUNT_JSON in Manage → Secrets to pull sessions and conversions.`,
      warn: ga4?.connected && ga4?.stale
        ? "No GA4 data received in the past 48 hours — the site tag may be broken or sending to a different property. Check that the live page source contains this property's measurement ID."
        : null,
    },
  ];

  return (
    <div className="mt-6 space-y-5" data-testid="connections-tab">
      <div className="grid sm:grid-cols-2 gap-5">
        {cards.map((c) => (
          <div key={c.title} className="bg-[#0D0D0F] border border-white/5 p-6" data-testid={c.testid}>
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2.5 font-heading font-extrabold uppercase text-base text-white tracking-tight">
                <c.icon size={16} className="text-[#9D0423]" /> {c.title}
              </span>
              <StatusPill ok={c.ok} />
            </div>
            <p className="mt-3 text-[12px] text-neutral-400 leading-relaxed">{c.body}</p>
            {c.warn && (
              <p className="mt-2 text-[11px] text-amber-400 leading-relaxed border border-amber-400/20 bg-amber-400/5 px-3 py-2" data-testid={`${c.testid}-warning`}>
                {c.warn}
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Semrush edit */}
        <Card title="Semrush Snapshot" right={<StatusPill ok okLabel="Manual entry" />} testid="conn-semrush">
          {sm && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                await saveSettings({
                  semrush: {
                    ...sm,
                    primary_keywords: Number(sm.primary_keywords || 0),
                    primary_traffic: Number(sm.primary_traffic || 0),
                    legacy_keywords: Number(sm.legacy_keywords || 0),
                    legacy_traffic: Number(sm.legacy_traffic || 0),
                  },
                });
              }}
              className="space-y-3"
            >
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls} htmlFor="sm-pk">{sm.primary_domain} keywords</label>
                  <input id="sm-pk" type="number" min="0" value={sm.primary_keywords} onChange={(e) => setSm({ ...sm, primary_keywords: e.target.value })} data-testid="semrush-primary-keywords" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="sm-pt">Est. monthly visits</label>
                  <input id="sm-pt" type="number" min="0" value={sm.primary_traffic} onChange={(e) => setSm({ ...sm, primary_traffic: e.target.value })} data-testid="semrush-primary-traffic" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="sm-lk">{sm.legacy_domain} keywords</label>
                  <input id="sm-lk" type="number" min="0" value={sm.legacy_keywords} onChange={(e) => setSm({ ...sm, legacy_keywords: e.target.value })} data-testid="semrush-legacy-keywords" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="sm-lt">Est. monthly visits</label>
                  <input id="sm-lt" type="number" min="0" value={sm.legacy_traffic} onChange={(e) => setSm({ ...sm, legacy_traffic: e.target.value })} data-testid="semrush-legacy-traffic" className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls} htmlFor="sm-note">Note</label>
                <input id="sm-note" value={sm.note} onChange={(e) => setSm({ ...sm, note: e.target.value })} data-testid="semrush-note" className={inputCls} />
              </div>
              <button type="submit" data-testid="semrush-save" className="w-full bg-[#9D0423] hover:bg-[#7E031C] text-white font-mono font-semibold text-[11px] uppercase tracking-[0.14em] py-3 transition-colors">
                Save Snapshot
              </button>
              <p className="text-[10px] text-neutral-600">Last updated {settings?.semrush?.updated_at} — saving stamps today's date.</p>
            </form>
          )}
        </Card>

        <div className="space-y-5">
          <UsersCard headers={headers} toast={toast} />

          {/* Settings */}
          <Card title="Dashboard Settings" right={<Settings2 size={15} className="text-neutral-500" />} testid="conn-settings">
            {gen && (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  await saveSettings({
                    avg_membership_value: Number(gen.avg_membership_value || 0),
                    speed_to_lead_target: Number(gen.speed_to_lead_target || 1),
                  });
                }}
                className="space-y-3"
              >
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls} htmlFor="set-amv">Avg membership value ($/mo)</label>
                    <input id="set-amv" type="number" min="0" value={gen.avg_membership_value} onChange={(e) => setGen({ ...gen, avg_membership_value: e.target.value })} data-testid="settings-avg-value" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="set-stl">Speed-to-lead target (min)</label>
                    <input id="set-stl" type="number" min="1" value={gen.speed_to_lead_target} onChange={(e) => setGen({ ...gen, speed_to_lead_target: e.target.value })} data-testid="settings-speed-target" className={inputCls} />
                  </div>
                </div>
                <button type="submit" data-testid="settings-save" className="w-full border border-white/20 hover:border-white/50 text-white font-mono font-semibold text-[11px] uppercase tracking-[0.14em] py-3 transition-colors">
                  Save Settings
                </button>
              </form>
            )}
          </Card>

          {/* Google setup instructions */}
          <Card title="Connect Google (GSC + GA4)" right={<LineChart size={15} className="text-neutral-500" />} testid="conn-google-setup">
            <div className="mb-3 border border-white/10 bg-[#121214] px-3 py-2.5" data-testid="google-sa-email">
              <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-neutral-500 mb-1">Service account (project big-dashboard-510018)</div>
              <div className="font-mono text-[11px] text-[#E56A79] break-all select-all">big-dashboard-reader@big-dashboard-510018.iam.gserviceaccount.com</div>
            </div>
            <ol className="space-y-2 text-[12px] text-neutral-300 leading-relaxed list-decimal list-inside">
              <li>In Search Console, add the email above as a user on <span className="font-mono text-[11px] text-white">sc-domain:bostonirongrittraining.com</span>.</li>
              <li>In GA4 Admin → Property access management, add the same email as <span className="text-white">Viewer</span> on property <span className="font-mono text-[11px] text-white">549599034</span>.</li>
              <li>
                Paste the service-account JSON key into{" "}
                <span className="font-mono text-[11px] text-[#E56A79]">GOOGLE_SERVICE_ACCOUNT_JSON</span> in Emergent → Manage → Secrets.
              </li>
            </ol>
            <p className="mt-3 text-[10px] text-neutral-600">GA4_PROPERTY_ID (549599034) and GSC_SITE_URL (sc-domain:bostonirongrittraining.com) are pre-configured as defaults. Credentials stay on the server and are never sent to the browser.</p>
          </Card>
        </div>
      </div>
    </div>
  );
}
